import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { SiteDashboardService } from '../../site-dashboard.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs/internal/Subscription';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { SitesService } from 'src/app/sites/sites.service';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-site-energy-report',
	templateUrl: './site-energy-report.component.html',
})

export class SiteEnergyReportComponent implements OnInit, OnChanges, OnDestroy {

	@Input() title: string = 'iotemeter_site_energy_report.site_energy_report';
	@Input() device: any;

	currentSite: any = {};
	siteSubscription: Subscription = new Subscription();

	readonly allYearsId = 'All-Years';
	readonly allQuartersId = 'All Quarters';
	readonly allMonthsId = 'All-Months';
	readonly allDevicesId = 'All-Devices';

	readonly periods = { MONTHLY: 'monthly', QUARTERLY: 'quarterly' };
	readonly periodTypes = [
		{ label: this.translate.instant('g.quarterly'), value: this.periods.QUARTERLY },
		{ label: this.translate.instant('g.monthly'), value: this.periods.MONTHLY },
	];

	installationDate = null;

	yearList: any[] = [];

	readonly devicesList = [
		{ label: this.translate.instant('iotemeter_site_energy_report.all_meters'), value: this.allDevicesId },
		{ label: this.translate.instant('iotemeter_site_energy_report.edge'), value: DeviceFactory.types.ioTEmeterEdge },
		{ label: this.translate.instant('iotemeter_site_energy_report.pro'), value: DeviceFactory.types.ioTEmeter },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp3'), value: DeviceFactory.types.ioTEmeterSp3 },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp1'), value: DeviceFactory.types.ioTEmeterSp1 },
	];

	selectedYear: string | number;
	selectedPeriodType: string = this.periods.MONTHLY;
	selectedDeviceType: string | number = this.allDevicesId;

	columnConfig: ColumnsConfig[] = [
		{ key: 'period', name: this.translate.instant('g.month'), type: 'string' },
		{ key: 'consumed_energy', name: this.translate.instant('devices.total_kWh'), type: 'number' },
		{ key: 'average_consumed_energy', name: this.translate.instant('iotemeter_site_energy_report.average_daily_kwh'), type: 'number' },
		{ key: 'peak_energy_demand', name: this.translate.instant('iotemeter_site_energy_report.max_demand_kw'), type: 'number' },
	];

	data: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		hideNoData: true,
		draggable: true,
		fileName: this.translate.instant(this.title),
		csvFileHeaderInfo: [],
	};

	constructor(
		private sitesService: SitesService,
		public siteDashboardService: SiteDashboardService,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private commonService: CommonService,
	) { }

	initializeData() {
		if (this.device?.config_info?.installation_date)
			this.installationDate = moment.utc(this.device.config_info.installation_date * 1000).startOf('day').toDate();

		const { yearList, currentYear } = this.siteDashboardService.prepareYearsList(this.yearList, this.installationDate);
		this.yearList = [{ label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId }, ...yearList];
		this.selectedYear = currentYear;
		this.selectedPeriodType = this.periods.MONTHLY;
		this.getIotemeterEnergyConsumption();
	}

	ngOnInit() {
		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.currentSite = siteInfo;
				this.installationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				this.initializeData();
			}
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['title'] && changes['title'].currentValue) {
			this.tableConfig.fileName = this.translate.instant(changes['title'].currentValue);
		}

		if (changes.device && !changes.device.firstChange) {
			this.device = changes.device.currentValue;
			this.initializeData();
		}
	}

	isAllYearsSelected() {
		return this.selectedYear === this.allYearsId;
	}

	getIotemeterEnergyConsumption() {
		const criteria: any = {
			siteId: this.currentSite.id,
			year: this.selectedYear,
			periodType: this.selectedPeriodType,
			quarter: this.allQuartersId,
			month: this.allMonthsId,
			deviceType: this.selectedDeviceType,
		};

		if (this.device)
			criteria.mac_address = this.device.config_info.mac_address;

		this.sitesService.getIotemeterEnergyConsumption(criteria).subscribe((response: { summary: any }) => {
			this.prepareData(response.summary);
		});
	}

	prepareData(responseData: any) {
		let data;

		switch (true) {
			case this.isAllYearsSelected():
				data = responseData?.years;
				break;
			case this.selectedPeriodType == this.periods.QUARTERLY:
				data = responseData?.quarters;
				break;
			case this.selectedPeriodType == this.periods.MONTHLY:
				data = responseData?.months;
				break;
			default:
				break;
		}

		const tableData = [];

		for (const period in data) {
			const periodValue = this.isAllYearsSelected() ? period : this.selectedPeriodType === this.periods.MONTHLY ? moment().month(Number(period) - 1).format('MMMM') : `Q${period}`;
			const rowValue = {
				period: { value: periodValue },
				consumed_energy: { value: data[period].consumed_energy || 0 },
				average_consumed_energy: { value: data[period].average_consumed_energy || 0 },
				peak_energy_demand: { value: data[period].peak_energy_demand || 0 },
			};

			tableData.push(rowValue);
		}

		this.prepareColumnConfig();
		this.data = tableData;

		this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device?.config_info?.mac_address,
			includeSpacing: false
		});
	}

	prepareColumnConfig() {
		const period = this.isAllYearsSelected() ? 'year' : this.selectedPeriodType === this.periods.MONTHLY ? 'month' : 'quarter';
		this.columnConfig[0] = { key: 'period', name: this.translate.instant(`g.${period}`), type: 'string' };
		this.columnConfig = structuredClone(this.columnConfig);
	}

	ngOnDestroy() {
		this.siteSubscription.unsubscribe();
	}
}
